export type GuestAccessNotice = {
  title: string;
  message: string;
};

/**
 * These notices are shown as the account/availability banner on the guest
 * page.  Keep this limited to states where sending someone to registration is
 * the right next step; stale edits and in-flight requests belong in a toast.
 */
export function getGuestAccessNotice(code: unknown): GuestAccessNotice | null {
  if (code === "GUEST_REGISTRATION_REQUIRED" || code === "GUEST_DESCRIPTION_ALREADY_ISSUED") {
    return {
      title: "Your free test quote has been used",
      message: "You’ve already used your free test quote. Create a free account to continue and generate more quotes.",
    };
  }

  if (code === "GUEST_SPEND_LIMIT") {
    return {
      title: "Free test quote unavailable",
      message: "Free test quotes are temporarily unavailable for this connection. Create a free account to continue and generate quotes.",
    };
  }

  return null;
}

/**
 * Translate guest API state into an actionable message without conflating a
 * consumed entitlement with a stale tab, an expired cookie, or a busy worker.
 * The API deliberately returns codes for these states so callers should not
 * fall back to the old generic "description approval required" copy.
 */
export function getGuestErrorNotice(code: unknown): GuestAccessNotice | null {
  switch (code) {
    case "GUEST_SESSION_REQUIRED":
      return {
        title: "Guest session not ready",
        message: "We’re preparing your free quote session. Please try again.",
      };
    case "GUEST_SESSION_INVALID":
    case "GUEST_SESSION_EXPIRED":
      return {
        title: "Your guest session has expired",
        message: "Start a new free test quote, or create a free account to continue.",
      };
    case "GUEST_DESCRIPTION_STALE":
      return {
        title: "Description needs review",
        message: "This description changed or is no longer editable. Review the refreshed description and try again.",
      };
    case "GUEST_DESCRIPTION_IN_PROGRESS":
    case "GUEST_GENERATION_IN_PROGRESS":
      return {
        title: "Quote is still being prepared",
        message: "Another request is already preparing this quote. Please wait a moment and try again.",
      };
    case "GUEST_DESCRIPTION_REQUIRED":
      return {
        title: "Description approval required",
        message: "Create or approve the project description before generating your quote.",
      };
    case "GUEST_DESCRIPTION_INVALID":
      return {
        title: "Description could not be approved",
        message: "Review the description and try again. The description must be plain text and no more than 12,000 characters.",
      };
    case "GUEST_ANALYSIS_REQUIRED":
      return {
        title: "Analysis required",
        message: "Complete quote analysis before saving this guest quote.",
      };
    case "GUEST_INPUT_UNAVAILABLE":
      return {
        title: "Uploaded file could not be read",
        message: "Try a clearer, unencrypted PDF or add a project description.",
      };
    default:
      return null;
  }
}